import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {z} from 'zod';
import {RequestParam} from '../../../../../incase-domain/requestParams';
import {useAddOrUpdateParam, useDeleteParam, useFetchPresParamValues} from '../queries';
import {IPresentationMeta} from '../../../../../incase-domain/presentation';
import {Param} from '../../../../../incase-domain/presentationParamValues/IPresentationParamValues';
import {createPlayPresentationUrl} from '../../../../../utils/presentation';
import PresentationLink from './PresentationLink';
import Button from '../../../../../incase-domain/components/button/Button';
import ConfirmModal from '../../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import PresenterLink from './PresenterLink';
import DataView, {ViewMode} from '../../../common/data/DataView';
import InputWithCharacterCounter from '../../../../../components/slides/highlighted/common/InputWithCharacterCounter';

interface IPresParamsProps {
    presentation: IPresentationMeta;
    open: boolean;
    roomId?: string;
    presenterLinkCode?: string;
}

function ViewerPresentationLinks({presentation, open, roomId, presenterLinkCode}: IPresParamsProps): JSX.Element {
    const {t} = useTranslation();

    const [values, setValues] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteModal, setDeleteModal] = useState(false);
    const [param, setParam] = useState<Param[] | null>(null);

    const {data: params} = useFetchPresParamValues({
        presentationId: presentation._id,
        enabled: open && !!presentation._id,
    });

    const paramKeys = useMemo(() => (presentation.requestParams ?? []).map((p) => p.key), [presentation.requestParams]);

    const {mutate: addOrUpdateParam} = useAddOrUpdateParam();
    const {mutate: deleteParam} = useDeleteParam();

    const schema = useMemo(() => {
        const shape: Record<string, z.ZodString | z.ZodOptional<z.ZodString>> = {};
        presentation.requestParams?.forEach((k) => {
            if (k.required) {
                shape[k.key] = z.string().trim().min(1, 'Required');
            } else {
                shape[k.key] = z.string().trim().optional();
            }
        });
        return z.object(shape);
    }, [presentation.requestParams]);

    const validateOne = useCallback(
        (name: string, val: string): boolean => {
            const field = schema.shape[name] as z.ZodString | undefined;
            if (!field) return true;
            const res = field.safeParse(val);
            setErrors((e) => ({...e, [name]: res.success ? '' : res.error.issues[0]?.message ?? 'Invalid'}));
            return res.success;
        },
        [schema]
    );

    const validateAll = useCallback((): boolean => {
        const res = schema.safeParse(values);

        if (res.success) {
            setErrors({});
            return true;
        }
        const flat = res.error.flatten().fieldErrors;
        const next: Record<string, string> = {};

        Object.keys(flat).forEach((k) => {
            next[k] = flat[k]?.[0] ?? 'Invalid';
        });

        setErrors(next);
        return false;
    }, [schema, values]);

    const handleSubmit = useCallback(
        (e?: React.FormEvent): void => {
            e?.preventDefault();
            if (!validateAll()) return;

            addOrUpdateParam({
                presentationId: presentation._id,
                newParam: Object.entries(values).map((el) => ({key: el[0], value: el[1]})),
            });

            setValues((prev) => {
                const cleared = {...prev};
                paramKeys.forEach((k) => {
                    cleared[k] = '';
                });
                return cleared;
            });
            setErrors({});
        },
        [validateAll, addOrUpdateParam, paramKeys, values, presentation._id]
    );

    const handleDelete = (presentationId: string, paramsToDelete: Param[]): void => {
        deleteParam({presentationId, paramsToDelete});
    };

    const viewersLinkWithoutReqParams = useMemo(() => {
        return createPlayPresentationUrl(presentation, false, roomId, false);
    }, [presentation, roomId]);

    async function handleCopyLink(copyLink: string): Promise<void> {
        try {
            if (copyLink) await navigator.clipboard.writeText(copyLink);
            toast.info(t('presEditPage.subNavBar.copiedToClipboard'));
        } catch (err) {
            toast.info(t('presEditPage.subNavBar.failedToCopy'));
        }
    }

    const showPresenterLink = !!roomId;

    const renderActions = (row: Param[]): JSX.Element => (
        <div className="u-flex u-gap-1">
            <Button
                trailing="solar:copy-linear"
                variant="soft"
                color="info"
                size="sm"
                onClick={() => {
                    const copyLink = createPlayPresentationUrl(
                        {
                            ...presentation,
                            requestParams: row as RequestParam[],
                        },
                        true,
                        roomId,
                        false
                    );
                    handleCopyLink(copyLink);
                }}
            >
                {t('editorPage.createPresentationLinksModal.copyLink')}
            </Button>
            <Button
                leading="solar:trash-bin-trash-linear"
                variant="ghost"
                color="error"
                square
                size="sm"
                onClick={() => {
                    setDeleteModal(true);
                    setParam(row);
                }}
            />
        </div>
    );

    const renderRequestParam = (row: Param[], key: string): JSX.Element => {
        const param = row?.find((p) => p.key === key);
        return <span>{param?.value || '-'}</span>;
    };

    const extraRow = useMemo(() => {
        const cells: Record<string, JSX.Element> = {};

        (presentation.requestParams ?? []).forEach((param, index) => {
            cells[param.key] = (
                <div>
                    <InputWithCharacterCounter
                        className="c-sidepanel__input"
                        key={`${param.key}-${index}`}
                        value={values[param.key] ?? ''}
                        placeholder={param.key}
                        onChange={(v) => {
                            setValues((s) => ({...s, [param.key]: v}));
                            if (errors[param.key]) validateOne(param.key, v);
                        }}
                        onBlur={(v) => validateOne(param.key, v)}
                        enableSubmitByEnter
                        onEnterPress={handleSubmit}
                        validator={() => !errors[param.key]}
                        data-cy={`extra_row_input_${param.key}`}
                    />
                </div>
            );
        });

        cells.actions = (
            <Button
                variant="ghost"
                size="xs"
                color="primary"
                leading="ph:plus"
                onClick={() => handleSubmit()}
                data-cy="extra_row_submit"
                fullWidth
            >
                Add
            </Button>
        );

        return cells;
    }, [values, errors, handleSubmit, validateOne, presentation.requestParams]);

    return (
        <>
            <div className="c-viewerPresentationLinks">
                {showPresenterLink && (
                    <div className="c-viewerPresentationLinks__presenter">
                        <Button leading="solar:microphone-2-linear" size="2xl" variant="ghost" disablePointerEvents />
                        <div className="c-viewerPresentationLinks__presenter_title">
                            {t('editorPage.createPresentationLinksModal.presenter')}
                        </div>
                        <div className="c-viewerPresentationLinks__presenter_description">
                            {t('editorPage.createPresentationLinksModal.linkBelowAllowsUserToJoinTheRoomAsPresenter')}
                        </div>

                        <div className="c-viewerPresentationLinks__presenter_button">
                            <PresenterLink presentation={presentation} roomId={roomId} presenterLinkCode={presenterLinkCode} />
                        </div>
                    </div>
                )}
                <div className="c-viewerPresentationLinks__presenter">
                    <Button leading="solar:users-group-two-rounded-linear" size="2xl" variant="ghost" disablePointerEvents />
                    <div className="c-viewerPresentationLinks__presenter_title">{t('editorPage.createPresentationLinksModal.viewers')}</div>
                    <div className="c-viewerPresentationLinks__presenter_description">
                        {t('editorPage.createPresentationLinksModal.differentiateAndGroupUserReportsByParameters')}
                    </div>

                    {presentation?.requestParams?.length ? (
                        <DataView
                            views={[
                                {
                                    columns: [
                                        ...(presentation.requestParams ?? []).map((requestParameter) => ({
                                            key: requestParameter.key,
                                            body: (row: Param[]) => renderRequestParam(row, requestParameter.key),
                                            label: requestParameter.key,
                                        })),
                                        {
                                            key: 'actions',
                                            body: (row: Param[]) => renderActions(row),
                                            width: '11rem',
                                        },
                                    ],
                                    view: ViewMode.TABLE,
                                },
                            ]}
                            rows={params?.params || []}
                            rowIdentifier={(param) => (param ? param[0].key || '' : '')}
                            extraRow={extraRow}
                        />
                    ) : (
                        <div className="c-viewerPresentationLinks__presenter_button">
                            <PresentationLink link={viewersLinkWithoutReqParams} />
                        </div>
                    )}
                </div>
            </div>
            <ConfirmModal
                isOpen={deleteModal}
                color="error"
                onCancel={() => {
                    setDeleteModal(false);
                    setParam(null);
                }}
                onConfirm={() => {
                    if (param) handleDelete(presentation._id, param);
                    setParam(null);
                    setDeleteModal(false);
                }}
                showCancel
                showConfirm
                message={t('editorPage.createPresentationLinksModal.deleteLinkConfirm')}
            />
        </>
    );
}

export default ViewerPresentationLinks;
